import { useNavigate } from 'react-router-dom';
import { CalendarClock, ExternalLink } from 'lucide-react';
import { WidgetContainer } from '../../infrastructure/widgets/WidgetContainer';
import { WidgetConfig } from '../../infrastructure/contracts/widget.contract';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { useUpcomingMeetings } from '@/features/meetings/hooks/useMeetings';
import type { Meeting, MeetingStatus } from '@/features/meetings/types';
import { ROUTES } from '@/routes/paths';

export const upcomingMeetingsConfig: WidgetConfig = {
  id: 'dash-upcoming-meetings',
  title: 'Upcoming Meetings',
  category: 'list',
  description: 'Your next scheduled meetings',
  refreshStrategy: 'interval',
  refreshIntervalMs: 120_000,
  defaultSize: { w: 6, h: 3 },
  priority: 3,
};

const STATUS_TONE: Record<MeetingStatus, BadgeTone> = {
  scheduled: 'info',
  completed: 'success',
  cancelled: 'neutral',
};

function whenLabel(m: Meeting): string {
  if (m.meeting_date) return m.meeting_time ? `${m.meeting_date} · ${m.meeting_time}` : m.meeting_date;
  if (m.scheduled_at) {
    const d = new Date(m.scheduled_at);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  }
  return 'Time TBD';
}

export function UpcomingMeetingsWidget() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useUpcomingMeetings();

  const meetings = (data ?? []).slice(0, 5);

  return (
    <WidgetContainer
      config={upcomingMeetingsConfig}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error?.message}
      onRefresh={() => {
        refetch();
      }}
    >
      {meetings.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <CalendarClock className="h-6 w-6 text-on-surface-variant" />
          <p className="text-body-sm text-on-surface-variant">No upcoming meetings.</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {meetings.map((m) => (
            <li
              key={m.id}
              onClick={() => navigate(ROUTES.meetings)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(ROUTES.meetings);
                }
              }}
              role="button"
              tabIndex={0}
              className="group flex cursor-pointer items-center justify-between gap-3 rounded-card-sm border border-outline bg-surface px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-DEFAULT bg-primary-container text-on-primary-container">
                  <CalendarClock className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-data-md font-medium text-on-surface">{m.title}</p>
                  <p className="truncate text-body-sm text-on-surface-variant">
                    {whenLabel(m)}
                    {m.organizer_name ? ` · ${m.organizer_name}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {m.link && (
                  <a
                    href={m.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-on-surface-variant opacity-0 transition-opacity hover:text-primary group-hover:opacity-100"
                    aria-label="Open meeting link"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <Badge tone={STATUS_TONE[m.status] ?? 'neutral'}>{m.status}</Badge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </WidgetContainer>
  );
}
